import { useEffect, useState } from 'react';
import { OpenCalculationReviewsResult, OpenCalcReviewsSearchRequest, SaveOpenCalculationReviewNotificationCommentRequest, SaveOpenCalculationReviewNotificationCommentResponse } from 'src/models/dashboard/OpenCalculationReview';

import apiService from '../ApiCallService';
import ErrorUtils from 'src/utils/ErrorUtils';
import DateUtils from 'src/utils/dateUtils';
import { OpenApprovalsResult, OpenApprovalsSearchRequest } from 'src/models/dashboard/OpenApproval';
import { OpenRequestsResult, OpenRequestsSearchRequest } from 'src/models/dashboard/OpenRequest';
import StringUtils from 'src/utils/stringUtils';
import { TasksSearchRequest, TasksSearchResult, BeginningBalanceTasksResponse } from 'src/models/dashboard/Task';
import { DbExpression } from 'src/models/common/TPESearchRequest';
import { NotificationSummary } from 'src/models/dashboard/NotificationSummary';
import NotificationComment from 'src/models/dashboard/NotificationComment';

/**
 * This class should only contain the API calls used in the dashboard
 */
export default class DashboardService {

    getCLIReviewNotificationComments(refreshComments: boolean, calculationNumber?: string): [any, any, any] {
        const [results, setResults] = useState<NotificationComment[]>([]);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.getCLIReviewNotificationComments(calculationNumber);
                    const json : any = await response.json();
                    json.comments.forEach((comment: NotificationComment) => {
                        comment.commentedDate = DateUtils.formatTimestamp(comment.commentedDate);
                    });
                    setResults(json.comments);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }

            if (!StringUtils.isNullOrEmpty(calculationNumber) && refreshComments) {
                fetchData();
            }
        }, [calculationNumber, refreshComments]);
        return [results, loading, error];
    }

    saveNotificationComment(payload: SaveOpenCalculationReviewNotificationCommentRequest | null): [SaveOpenCalculationReviewNotificationCommentResponse | undefined, boolean, string] {
        const [results, setResults] = useState<SaveOpenCalculationReviewNotificationCommentResponse | undefined>(undefined);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.saveNotificationComment(payload);
                    const json : SaveOpenCalculationReviewNotificationCommentResponse = await response.json();
                    setResults(json);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }

            if (payload) {
                fetchData();
            }
        }, [payload]);
        return [results, loading, error];
    }
    

    getNotificationSummary(query: string) : [NotificationSummary, boolean, string] {
        const [results, setResults] = useState(null as unknown as NotificationSummary);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.getNotificationSummary();
                    const json : NotificationSummary = await response.json();
                    setResults(json);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }

            if (!StringUtils.isNullOrEmpty(query)) {
                fetchData();
            }
        }, [query]);
        return [results, loading, error];
    }

    searchOpenCalculationReviews(payload: OpenCalcReviewsSearchRequest | undefined) : [OpenCalculationReviewsResult, boolean, string] {
        const [results, setResults] = useState(null as unknown as OpenCalculationReviewsResult);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.searchOpenCalculationReviews(payload);
                    const json : OpenCalculationReviewsResult = await response.json();
                    if (json.items != null) {
                        json.items.forEach(i => i.dueDateDisplay = DateUtils.formatDate(i.dueDate, 'YYYY-MM-DD'));
                    }
                    setResults(json);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }

            if (payload != null) {
                fetchData();
            }
        }, [payload]);
        return [results, loading, error];
    }

    searchApprovals(payload: OpenApprovalsSearchRequest | undefined) : [OpenApprovalsResult, boolean, string] {
        const [results, setResults] = useState(null as unknown as OpenApprovalsResult);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.searchApprovals(payload);
                    const json : OpenApprovalsResult = await response.json();
                    if (json.items != null) {
                        json.items.forEach(i => i.dueDateDisplay = DateUtils.formatDate(i.dueDate, 'YYYY-MM-DD'));
                    }
                    setResults(json);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }

            if (payload != null) {
                fetchData();
            }
        }, [payload]);
        return [results, loading, error];
    }

    searchRequests(payload: OpenRequestsSearchRequest | undefined) : [OpenRequestsResult, boolean, string] {
        const [results, setResults] = useState(null as unknown as OpenRequestsResult);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.searchRequests(payload);
                    const json : OpenRequestsResult = await response.json();
                    if (json.items != null) {
                        json.items.forEach(i => i.dueDateDisplay = DateUtils.formatDate(i.dueDate, 'YYYY-MM-DD'));
                    }
                    setResults(json);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }

            if (payload != null) {
                fetchData();
            }
        }, [payload]);
        return [results, loading, error];
    }
    
    
    searchTasks(payload: TasksSearchRequest | undefined): [any, any, any] {
        const [results, setResults] = useState(null as unknown as TasksSearchResult);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.searchTasks(payload);
                    const json : TasksSearchResult = await response.json();
                    if (json.items != null) {
                        json.items.forEach(i => {
                            i.dueDateDisplay = DateUtils.formatDate(i.dueDate, 'YYYY-MM-DD');
                            if (i.entityName == null) {
                                i.entityName = i.entityId;
                            }
                        });  
                    }
                    setResults(json);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }

            if (payload != null) {
                fetchData();
            }
        }, [payload]);
        return [results, loading, error];
    }

    markNotificationsComplete(payload: any): [any, any, any] {
        const [results, setResults] = useState(null as unknown as TasksSearchResult);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function postData() {
                try {
                    setLoading(true);
                    const response = await apiService.markNotificationsComplete(payload);
                    const json = await response.json();
                    setResults(json);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }

            if (payload != null) {
                postData();
            }
        }, [payload]);
        return [results, loading, error];
    }

    getBeginningBalanceTasks(calculationNumber: string | null, refreshCounter: number): [BeginningBalanceTasksResponse | null, boolean, string] {
        const [results, setResults] = useState<BeginningBalanceTasksResponse | null>(null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.getBeginningBalanceTasks(calculationNumber!);
                    const json: BeginningBalanceTasksResponse = await response.json();
                    setResults(json);
                }
                catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                }
                finally {
                    setLoading(false);
                }
            }

            if (calculationNumber != null) {
                fetchData();
            } else {
                setResults(null);
                setError('');
            }
        }, [calculationNumber, refreshCounter]);
        return [results, loading, error];
    }

}


